import treasuryChartResize from './treasuryChartResize';
import TreasuryTable from './TreasuryTable';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchAPI, postAPI, putAPI, deleteAPI } from '../../utils/utils'; // postAPI still used for pause/resume/cancel
import { can } from '../../utils/permissions';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './SubscriptionsTab.css';
import { getCastellerFullName, getCastellerNameWithMote } from '../../utils/castellerName';
import { formatMoney } from '../../utils/money';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

// Cadence configuration for recurring subscriptions
const CADENCE_OPTIONS = [
    { value: 'monthly', label: 'Mensual', months: 1, perLabel: '/mes' },
    { value: 'quarterly', label: 'Trimestral', months: 3, perLabel: '/trimestre' },
    { value: 'semiannual', label: 'Semestral', months: 6, perLabel: '/semestre' },
    { value: 'annual', label: 'Anual', months: 12, perLabel: '/any' }
];

const getCadenceOption = (cadence) => CADENCE_OPTIONS.find(o => o.value === cadence) || CADENCE_OPTIONS[0];

/**
 * SubscriptionsTab - Manages recurring subscription quotes
 *
 * Displays:
 * 1. Summary stats (active/paused subscribers, monthly revenue equivalent)
 * 2. List of subscription quotes with configurable cadence
 * 3. Subscriber details with admin controls
 * 4. Form to create new subscription quotes with cadence selection
 * 5. History of subscription changes
 */
function SubscriptionsTab({ userInfo, castellersInfo, stripeConnectStatus }) {
    const [subscriptionQuotes, setSubscriptionQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedQuota, setExpandedQuota] = useState(null);
    const [quotaSubscribers, setQuotaSubscribers] = useState({});
    const [loadingSubscribers, setLoadingSubscribers] = useState({});
    const [actionLoading, setActionLoading] = useState(null);
    const [monthlyRevenue, setMonthlyRevenue] = useState(null);
    const [effectiveDonations, setEffectiveDonations] = useState([]);
    const [effectiveDonationsLoading, setEffectiveDonationsLoading] = useState(false);
    const [effectiveDonationsError, setEffectiveDonationsError] = useState(null);
    const [showEffectiveDonations, setShowEffectiveDonations] = useState(true);
    
    // Edit quota state
    const [editingQuotaId, setEditingQuotaId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    // Delete quota state
    const [deleteLoading, setDeleteLoading] = useState(null);

    // Cens de socis export state
    const [censLoading, setCensLoading] = useState(false);

    // History state
    const [showHistory, setShowHistory] = useState(true);
    const [historyEvents, setHistoryEvents] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(0);
    const HISTORY_PAGE_SIZE = 20;

    // Fetch monthly revenue data
    const fetchMonthlyRevenue = useCallback(() => {
        fetchAPI('/subscriptions/monthly-revenue', (data) => {
            if (data?.success) {
                setMonthlyRevenue(data);
            }
        });
    }, []);

    const fetchEffectiveDonations = useCallback(() => {
        setEffectiveDonationsLoading(true);
        setEffectiveDonationsError(null);
        fetchAPI('/subscriptions/donations?limit=100&offset=0', (data) => {
            if (data?.success) {
                setEffectiveDonations(data.donations || []);
                setEffectiveDonationsError(null);
            } else {
                setEffectiveDonations([]);
                setEffectiveDonationsError(data?.message || 'Error carregant les donacions efectuades');
            }
            setEffectiveDonationsLoading(false);
        });
    }, []);

    // Fetch subscription quotes with variants
    const fetchSubscriptionQuotes = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchAPI('/subscriptions/quotes', (data) => {
            if (data?.success) {
                const quotes = data.quotes || [];
                // If backend doesn't return variants, fetch them separately
                if (quotes.length > 0 && !quotes[0].variants) {
                    // Fetch variants for each quote
                    fetchAPI('/quotes/variants/batch?ids=' + quotes.map(q => q.id).join(','), (variantsData) => {
                        if (variantsData?.success) {
                            const variantsByQuota = {};
                            (variantsData.variants || []).forEach(v => {
                                if (!variantsByQuota[v.quota_id]) {
                                    variantsByQuota[v.quota_id] = [];
                                }
                                variantsByQuota[v.quota_id].push(v);
                            });
                            const quotesWithVariants = quotes.map(q => ({
                                ...q,
                                variants: variantsByQuota[q.id] || []
                            }));
                            setSubscriptionQuotes(quotesWithVariants);
                        } else {
                            setSubscriptionQuotes(quotes);
                        }
                        setLoading(false);
                    }, () => {
                        setSubscriptionQuotes(quotes);
                        setLoading(false);
                    });
                } else {
                    setSubscriptionQuotes(quotes);
                    setLoading(false);
                }
            } else {
                setError(data?.message || 'Error carregant les quotes de subscripció');
                setLoading(false);
            }
        }, (err) => {
            setError('Error de connexió');
            setLoading(false);
        });
    }, []);

    // Fetch subscribers for a specific quota
    const fetchSubscribers = useCallback((quotaId) => {
        setLoadingSubscribers(prev => ({ ...prev, [quotaId]: true }));
        fetchAPI(`/subscriptions/quota/${quotaId}`, (data) => {
            if (data?.success) {
                setQuotaSubscribers(prev => ({ ...prev, [quotaId]: data.subscriptions || [] }));
            }
            setLoadingSubscribers(prev => ({ ...prev, [quotaId]: false }));
        }, () => {
            setLoadingSubscribers(prev => ({ ...prev, [quotaId]: false }));
        });
    }, []);

    // Fetch subscription history
    const fetchHistory = useCallback((page = 0) => {
        setHistoryLoading(true);
        const offset = page * HISTORY_PAGE_SIZE;
        fetchAPI(`/subscriptions/history?limit=${HISTORY_PAGE_SIZE}&offset=${offset}`, (data) => {
            if (data?.success) {
                setHistoryEvents(data.events || []);
                setHistoryTotal(data.total || 0);
                setHistoryPage(page);
            }
            setHistoryLoading(false);
        }, () => {
            setHistoryLoading(false);
        });
    }, []);

    useEffect(() => {
        fetchMonthlyRevenue();
        fetchSubscriptionQuotes();
        fetchEffectiveDonations();
    }, [fetchMonthlyRevenue, fetchSubscriptionQuotes, fetchEffectiveDonations]);

    // Auto-expand first subscription quote by default
    useEffect(() => {
        if (subscriptionQuotes.length > 0 && expandedQuota === null) {
            const firstQuoteId = subscriptionQuotes[0].id;
            setExpandedQuota(firstQuoteId);
            if (!quotaSubscribers[firstQuoteId]) {
                fetchSubscribers(firstQuoteId);
            }
        }
    }, [subscriptionQuotes, expandedQuota, quotaSubscribers, fetchSubscribers]);

    // Fetch history when showing history section
    useEffect(() => {
        if (showHistory && historyEvents.length === 0) {
            fetchHistory(0);
        }
    }, [showHistory, historyEvents.length, fetchHistory]);

    // Toggle expanded quota
    const toggleQuotaExpanded = (quotaId) => {
        if (expandedQuota === quotaId) {
            setExpandedQuota(null);
        } else {
            setExpandedQuota(quotaId);
            if (!quotaSubscribers[quotaId]) {
                fetchSubscribers(quotaId);
            }
        }
    };

    // Admin actions
    const handlePause = async (subscriptionId, quotaId) => {
        if (!window.confirm('Segur que vols pausar aquesta subscripció?')) return;
        
        setActionLoading(subscriptionId);
        postAPI(`/subscriptions/${subscriptionId}/pause`, {}, (data) => {
            if (data?.success) {
                fetchSubscribers(quotaId);
                fetchMonthlyRevenue();
            } else {
                alert(data?.message || 'Error pausant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    const handleResume = async (subscriptionId, quotaId) => {
        setActionLoading(subscriptionId);
        postAPI(`/subscriptions/${subscriptionId}/resume`, {}, (data) => {
            if (data?.success) {
                fetchSubscribers(quotaId);
                fetchMonthlyRevenue();
            } else {
                alert(data?.message || 'Error reactivant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    const handleCancel = async (subscriptionId, quotaId) => {
        if (!window.confirm('Segur que vols cancel·lar aquesta subscripció? Aquesta acció no es pot desfer.')) return;
        
        setActionLoading(subscriptionId);
        postAPI(`/subscriptions/${subscriptionId}/cancel`, {}, (data) => {
            if (data?.success) {
                fetchSubscribers(quotaId);
                fetchMonthlyRevenue();
            } else {
                alert(data?.message || 'Error cancel·lant la subscripció');
            }
            setActionLoading(null);
        }, () => {
            alert('Error de connexió');
            setActionLoading(null);
        });
    };

    // Edit quota title
    const handleStartEdit = (quote, e) => {
        e.stopPropagation();
        setEditingQuotaId(quote.id);
        setEditTitle(quote.titol);
    };

    const handleCancelEdit = (e) => {
        e.stopPropagation();
        setEditingQuotaId(null);
        setEditTitle('');
    };

    const handleSaveEdit = (quotaId, e) => {
        e.stopPropagation();
        if (!editTitle.trim()) return;

        setEditLoading(true);
        putAPI(`/quotes/${quotaId}`, { titol: editTitle.trim() }, (data) => {
            if (data?.success) {
                setSubscriptionQuotes(prev => 
                    prev.map(q => q.id === quotaId ? { ...q, titol: editTitle.trim() } : q)
                );
                setEditingQuotaId(null);
                setEditTitle('');
            } else {
                alert(data?.message || 'Error actualitzant el títol');
            }
            setEditLoading(false);
        }, () => {
            alert('Error de connexió');
            setEditLoading(false);
        });
    };

    // Delete subscription quote
    const handleDeleteQuote = (quote, e) => {
        e.stopPropagation();

        if (quote.active_subscribers > 0) {
            alert('No es pot eliminar una quota amb subscriptors actius');
            return;
        }

        if (!window.confirm(`Segur que vols eliminar la quota "${quote.titol}"? Aquesta acció no es pot desfer.`)) {
            return;
        }

        setDeleteLoading(quote.id);
        deleteAPI(`/subscriptions/quote/${quote.id}`, (data) => {
            if (data?.success) {
                fetchSubscriptionQuotes();
                fetchMonthlyRevenue();
            } else {
                alert(data?.message || 'Error eliminant la quota');
            }
            setDeleteLoading(null);
        }, () => {
            alert('Error de connexió');
            setDeleteLoading(null);
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ca-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Download the current member census (soci = 1) as a CSV file
    const handleDownloadCens = () => {
        setCensLoading(true);
        fetchAPI('/subscriptions/socis-cens', (data) => {
            setCensLoading(false);
            if (!data?.success) {
                alert(data?.message || 'Error carregant el cens de socis');
                return;
            }

            const statusLabels = {
                active: 'Activa',
                paused: 'Pausada',
                cancelled: 'Cancel·lada',
                past_due: 'Pagament pendent',
            };
            const escapeCell = (value) => {
                const text = value === null || value === undefined ? '' : String(value);
                return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
            };
            const csvDate = (value) => (value ? formatDate(value) : '');

            const header = ['Nom', 'Primer cognom', 'Segon cognom', 'Sobrenom', 'Correu', 'Subscripció', 'Variant', 'Adults coberts', 'Nens coberts', 'Cobert per', 'Estat subscripció', 'Data alta', 'Últim pagament', 'Pròxim pagament'];
            const rows = (data.socis || []).map(soci => [
                soci.nom,
                soci.primer_cognom,
                soci.segon_cognom,
                soci.mote,
                soci.mail,
                soci.quota_titol || soci.covered_by_quota,
                soci.variant_nom || soci.covered_by_variant,
                soci.quota_titol ? (soci.num_adults ?? '') : '',
                soci.quota_titol ? (soci.num_children ?? '') : '',
                soci.covered_by || '',
                soci.subscription_status ? (statusLabels[soci.subscription_status] || soci.subscription_status) : '',
                csvDate(soci.start_date),
                csvDate(soci.last_payment_date),
                csvDate(soci.next_payment_date),
            ]);

            // BOM + semicolons so Excel (locale ca/es) opens it directly
            const csvContent = '\uFEFF' + [header, ...rows]
                .map(row => row.map(escapeCell).join(';'))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const today = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.download = `cens-socis-${today}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, false, () => {
            setCensLoading(false);
            alert('Error de connexió carregant el cens de socis');
        });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="subscription-badge active">Activa</span>;
            case 'paused':
                return <span className="subscription-badge paused">Pausada</span>;
            case 'cancelled':
                return <span className="subscription-badge cancelled">Cancel·lada</span>;
            case 'past_due':
                return <span className="subscription-badge past-due">Pagament pendent</span>;
            default:
                return <span className="subscription-badge">{status}</span>;
        }
    };

    // Get event type badge and info
    const getEventInfo = (eventType) => {
        switch (eventType) {
            case 'subscribed':
                return { 
                    badge: <span className="sub-event-badge subscribed"><i className="fas fa-user-plus"></i> Alta</span>,
                    icon: 'fa-user-plus',
                    color: '#10b981'
                };
            case 'paused':
                return { 
                    badge: <span className="sub-event-badge paused"><i className="fas fa-pause-circle"></i> Pausa</span>,
                    icon: 'fa-pause-circle',
                    color: '#f59e0b'
                };
            case 'resumed':
                return { 
                    badge: <span className="sub-event-badge resumed"><i className="fas fa-play-circle"></i> Reactivada</span>,
                    icon: 'fa-play-circle',
                    color: '#3b82f6'
                };
            case 'cancelled':
                return { 
                    badge: <span className="sub-event-badge cancelled"><i className="fas fa-user-times"></i> Baixa</span>,
                    icon: 'fa-user-times',
                    color: '#dc2626'
                };
            default:
                return { 
                    badge: <span className="sub-event-badge">{eventType}</span>,
                    icon: 'fa-circle',
                    color: '#64748b'
                };
        }
    };

    // Format datetime
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ca-ES', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get casteller name
    const getCastellerName = (subscriber) => {
        const name = subscriber.nom || '';
        const cognom = subscriber.cognom || '';
        const mote = subscriber.mote;
        
        if (mote) {
            return `${name} ${cognom} (${mote})`;
        }
        return `${name} ${cognom}`;
    };

    // Chart data for monthly revenue
    const monthNames = ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des'];
    
    const chartData = useMemo(() => {
        if (!monthlyRevenue?.monthlyData) return null;
        
        const data = monthlyRevenue.monthlyData;
        
        const labels = data.map(d => {
            const [year, month] = d.month.split('-');
            return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
        });
        
        const subscriptionRevenues = data.map(d => d.base || 0);
        const donationRevenues = data.map(d => d.donations || 0);
        const isProjected = data.map(d => d.isProjected);
        const isCurrent = data.map(d => d.isCurrent);
        
        return {
            labels,
            datasets: [
                {
                    label: 'Subscripcions',
                    data: subscriptionRevenues,
                    backgroundColor: data.map(d => {
                        if (d.isFuture) return 'rgba(99, 102, 241, 0.4)';
                        if (d.isCurrent && d.isProjected) return 'rgba(99, 102, 241, 0.6)';
                        return 'rgba(99, 102, 241, 0.85)';
                    }),
                    borderColor: data.map(d => d.isProjected ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 1)'),
                    borderWidth: 2,
                    borderRadius: {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 6,
                        bottomRight: 6
                    },
                    borderDash: data.map(d => d.isProjected ? [4, 4] : []),
                    maxBarThickness: 50,
                    stack: 'income'
                },
                {
                    label: 'Donacions',
                    data: donationRevenues,
                    backgroundColor: data.map(d => {
                        if (d.isFuture) return 'rgba(168, 85, 247, 0.35)';
                        if (d.isCurrent && d.isProjected) return 'rgba(168, 85, 247, 0.5)';
                        return 'rgba(147, 51, 234, 0.78)';
                    }),
                    borderColor: data.map(d => d.isProjected ? 'rgba(126, 34, 206, 0.55)' : 'rgba(126, 34, 206, 0.95)'),
                    borderWidth: 2,
                    borderRadius: 6,
                    borderDash: data.map(d => d.isProjected ? [4, 4] : []),
                    maxBarThickness: 50,
                    stack: 'income'
                }
            ],
            rawData: data,
            donationRevenues,
            isProjected,
            isCurrent
        };
    }, [monthlyRevenue]);
    
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        onResize: treasuryChartResize,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#475569',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    title: (contexts) => {
                        const ctx = contexts[0];
                        const dataPoint = chartData?.rawData?.[ctx.dataIndex];
                        if (!dataPoint) return '';
                        const [year, month] = dataPoint.month.split('-');
                        return `${monthNames[parseInt(month) - 1]} ${year}`;
                    },
                    label: (context) => {
                        const dataPoint = chartData?.rawData?.[context.dataIndex];
                        const value = context.parsed.y || 0;
                        const label = dataPoint?.isProjected ? 'Projectat' : 'Confirmat';
                        const datasetLabel = context.dataset?.label || 'Ingressos';
                        return `${datasetLabel} (${label}): ${formatMoney(value)}€`;
                    },
                    afterLabel: (context) => {
                        if (context.datasetIndex !== 0) return '';
                        const dataPoint = chartData?.rawData?.[context.dataIndex];
                        if (!dataPoint) return '';
                        if (dataPoint.count > 0) {
                            return `${dataPoint.count} pagament${dataPoint.count > 1 ? 's' : ''}`;
                        }
                        return '';
                    }
                }
            },
            datalabels: {
                display: 'auto',
                anchor: 'end',
                align: 'top',
                formatter: (value, context) => {
                    const idx = context.dataIndex;
                    const total = chartData?.rawData?.[idx]?.total || 0;
                    const donations = chartData?.donationRevenues?.[idx] || 0;

                    const isDonationSegment = context.datasetIndex === 1;
                    const isTopSegment = isDonationSegment ? value > 0 : donations <= 0 && value > 0;
                    return isTopSegment && total > 0 ? `${total.toFixed(0)}€` : '';
                },
                color: '#475569',
                font: { size: 11, weight: '600' },
                padding: 4
            }
        },
        scales: {
            x: {
                grid: { display: false },
                stacked: true,
                ticks: {
                    color: '#64748b',
                    font: { size: 11, weight: '500' }
                }
            },
            y: {
                beginAtZero: true,
                stacked: true,
                grid: { color: '#e2e8f0' },
                border: { display: false },
                ticks: {
                    color: '#64748b',
                    font: { size: 11 },
                    padding: 8,
                    callback: (value) => formatMoney(value) + '€'
                }
            }
        },
        layout: { padding: { top: 30 } }
    }), [chartData]);

    if (loading) {
        return (
            <div className="subscriptions-tab">
                <div className="subscriptions-loading">
                    <i className="fas fa-spinner fa-spin"></i> Carregant subscripcions...
                </div>
            </div>
        );
    }

    return (
        <div className="subscriptions-tab">
            <div className="subscriptions-tab-header">
                <h2>Subscripcions recurrents</h2>
                {can(userInfo, 'MANAGE_QUOTES') ? (
                    <button
                        className="socis-cens-export-btn"
                        onClick={handleDownloadCens}
                        disabled={censLoading}
                        title="Descarrega un CSV amb tots els socis actuals i la seva subscripció"
                    >
                        {censLoading
                            ? <><i className="fas fa-spinner fa-spin"></i> Generant cens...</>
                            : <><i className="fas fa-file-csv"></i> Descarregar cens de socis</>}
                    </button>
                ) : null}
            </div>
            <p className="subscriptions-description">
                Gestiona les subscripcions recurrents i els seus subscriptors. Per crear una nova quota recurrent, utilitza el formulari de "Crear nova".
            </p>

            {error ? (
                <div className="subscriptions-error">
                    <i className="fas fa-exclamation-triangle"></i> {error}
                    <button onClick={fetchSubscriptionQuotes} className="retry-button">
                        Tornar a provar
                    </button>
                </div>
            ) : null}

            {/* Monthly Revenue Chart */}
            {chartData ? (
                <div className="subscriptions-revenue-chart">
                    <div className="chart-header">
                        <h3><i className="fas fa-chart-bar"></i> Ingressos per subscripcions</h3>
                        {monthlyRevenue?.activeSubscribers > 0 ? (
                            <div className="chart-summary">
                                <span className="summary-item">
                                    <i className="fas fa-users"></i>
                                    {monthlyRevenue.activeSubscribers} subscriptor{monthlyRevenue.activeSubscribers !== 1 ? 's' : ''} actiu{monthlyRevenue.activeSubscribers !== 1 ? 's' : ''}
                                </span>
                                <span className="summary-item projected">
                                    <i className="fas fa-arrow-trend-up"></i>
                                    {formatMoney(monthlyRevenue.projectedMonthly * 12)}€/any
                                </span>
                            </div>
                        ) : null}
                    </div>
                    <div className="chart-container">
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                    <div className="chart-legend">
                        <span className="legend-item subscriptions"><span className="legend-color"></span> Subscripcions</span>
                        <span className="legend-item donations"><span className="legend-color"></span> Donacions</span>
                        <span className="legend-item projected"><span className="legend-color projected-style"></span> Projectat (translúcid)</span>
                    </div>
                </div>
            ) : null}

            {/* Subscription Quotes List */}
            <div className="subscriptions-section">
                <h3>Quotes amb subscripció</h3>
                
                {subscriptionQuotes.length === 0 ? (
                    <div className="subscriptions-empty">
                        <i className="fas fa-info-circle"></i>
                        <p>No hi ha quotes de subscripció creades.</p>
                        {can(userInfo, 'MANAGE_QUOTES') ? (
                            <p className="hint">Utilitza la pestanya "Crear nova" i marca l'opció "Quota recurrent".</p>
                        ) : (
                            <p className="hint">Contacta amb un cap de junta per crear quotes de subscripció.</p>
                        )}
                    </div>
                ) : (
                    <div className="subscription-quotes-list">
                        {subscriptionQuotes.map(quote => (
                            <div key={quote.id} className="subscription-quote-card">
                                <div 
                                    className="quote-header"
                                    onClick={() => editingQuotaId !== quote.id ? toggleQuotaExpanded(quote.id) : null}
                                >
                                    <div className="quote-info">
                                        {editingQuotaId === quote.id ? (
                                            <div className="edit-title-row" onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    className="edit-title-input"
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEdit(quote.id, e);
                                                        if (e.key === 'Escape') handleCancelEdit(e);
                                                    }}
                                                    disabled={editLoading}
                                                    autoFocus
                                                />
                                                <button
                                                    className="edit-action-btn save"
                                                    onClick={(e) => handleSaveEdit(quote.id, e)}
                                                    disabled={editLoading || !editTitle.trim()}
                                                    title="Desar"
                                                >
                                                    {editLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                                </button>
                                                <button
                                                    className="edit-action-btn cancel"
                                                    onClick={handleCancelEdit}
                                                    disabled={editLoading}
                                                    title="Cancel·lar"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="title-row">
                                                <h4>{quote.titol}</h4>
                                                {can(userInfo, 'MANAGE_QUOTES') ? (
                                                    <>
                                                        <button
                                                            className="edit-title-btn"
                                                            onClick={(e) => handleStartEdit(quote, e)}
                                                            title="Editar nom"
                                                        >
                                                            <i className="fas fa-pencil-alt"></i>
                                                        </button>
                                                        <button
                                                            className="delete-quote-btn"
                                                            onClick={(e) => handleDeleteQuote(quote, e)}
                                                            disabled={(quote.active_subscribers || 0) > 0 || deleteLoading === quote.id}
                                                            title={(quote.active_subscribers || 0) > 0
                                                                ? 'No es pot eliminar amb subscriptors actius'
                                                                : 'Eliminar quota'}
                                                        >
                                                            {deleteLoading === quote.id
                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                : <i className="fas fa-trash-alt"></i>}
                                                        </button>
                                                    </>
                                                ) : null}
                                            </div>
                                        )}
                                        <div className="quote-prices">
                                            <span className="cadence-badge">{getCadenceOption(quote.cadence).label}</span>
                                            {quote.variants && quote.variants.length > 0 ? (
                                                <span className="variants-count">
                                                    <i className="fas fa-layer-group"></i> {quote.variants.length} variants
                                                </span>
                                            ) : (
                                                <span className="quote-amount">{formatMoney(quote.import)}€{getCadenceOption(quote.cadence).perLabel}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="quote-stats">
                                        <span className="subscribers-count active">
                                            <i className="fas fa-user-check"></i> {quote.active_subscribers || 0} actius
                                        </span>
                                        <span className="subscribers-count paused">
                                            <i className="fas fa-user-clock"></i> {quote.paused_subscribers || 0} pausats
                                        </span>
                                    </div>
                                    <i className={`fas fa-chevron-${expandedQuota === quote.id ? 'up' : 'down'} expand-icon`}></i>
                                </div>
                                
                                {expandedQuota === quote.id ? (
                                    <div className="quote-subscribers">
                                        {loadingSubscribers[quote.id] ? (
                                            <div className="loading-subscribers">
                                                <i className="fas fa-spinner fa-spin"></i> Carregant subscriptors...
                                            </div>
                                        ) : quotaSubscribers[quote.id]?.length === 0 ? (
                                            <div className="no-subscribers">
                                                <i className="fas fa-users-slash"></i>
                                                <p>Aquesta quota encara no té subscriptors.</p>
                                            </div>
                                        ) : (
                                            <TreasuryTable label="Subscriptors" className="subscribers-table-wrapper">
                                            <table className="subscribers-table">
                                                <thead>
                                                    <tr>
                                                        <th>Casteller</th>
                                                        <th>Variant</th>
                                                        <th>Estat</th>
                                                        <th>Donatiu programat</th>
                                                        <th>Data inici</th>
                                                        <th>Pròxim pagament</th>
                                                        {can(userInfo, 'MANAGE_QUOTES') ? <th>Accions</th> : null}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {quotaSubscribers[quote.id]?.map(subscriber => (
                                                        <tr key={subscriber.id} className={`status-${subscriber.status}`}>
                                                            <td className="subscriber-name">
                                                                {getCastellerName(subscriber)}
                                                            </td>
                                                            <td className="variant-cell">
                                                                {subscriber.variant_nom || '-'}
                                                            </td>
                                                            <td>{getStatusBadge(subscriber.status)}</td>
                                                            <td>
                                                                {parseFloat(subscriber.donation_amount) > 0 
                                                                    ? `+${formatMoney(subscriber.donation_amount)}€` 
                                                                    : '-'}
                                                            </td>
                                                            <td>{formatDate(subscriber.start_date)}</td>
                                                            <td>{formatDate(subscriber.next_payment_date)}</td>
                                                            {can(userInfo, 'MANAGE_QUOTES') ? (
                                                                <td className="subscriptions-actions-cell">
                                                                    {subscriber.status === 'active' ? (
                                                                        <button
                                                                            className="action-btn pause"
                                                                            onClick={() => handlePause(subscriber.id, quote.id)}
                                                                            disabled={actionLoading === subscriber.id}
                                                                            title="Pausar subscripció"
                                                                        >
                                                                            {actionLoading === subscriber.id 
                                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                                : <i className="fas fa-pause"></i>}
                                                                        </button>
                                                                    ) : subscriber.status === 'paused' ? (
                                                                        <button
                                                                            className="action-btn resume"
                                                                            onClick={() => handleResume(subscriber.id, quote.id)}
                                                                            disabled={actionLoading === subscriber.id}
                                                                            title="Reactivar subscripció"
                                                                        >
                                                                            {actionLoading === subscriber.id 
                                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                                : <i className="fas fa-play"></i>}
                                                                        </button>
                                                                    ) : null}
                                                                    {subscriber.status !== 'cancelled' ? (
                                                                        <button
                                                                            className="action-btn cancel"
                                                                            onClick={() => handleCancel(subscriber.id, quote.id)}
                                                                            disabled={actionLoading === subscriber.id}
                                                                            title="Cancel·lar subscripció"
                                                                        >
                                                                            {actionLoading === subscriber.id 
                                                                                ? <i className="fas fa-spinner fa-spin"></i>
                                                                                : <i className="fas fa-times"></i>}
                                                                        </button>
                                                                    ) : null}
                                                                </td>
                                                            ) : null}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            </TreasuryTable>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="subscriptions-section effective-donations-section">
                <div className="effective-donations-card">
                    <div
                        className="effective-donations-header"
                        onClick={() => setShowEffectiveDonations(!showEffectiveDonations)}
                    >
                        <h3>
                            <i className="fas fa-hand-holding-heart"></i> Donacions efectuades
                        </h3>
                        <i className={`fas fa-chevron-${showEffectiveDonations ? 'up' : 'down'} expand-icon`}></i>
                    </div>

                    {showEffectiveDonations ? (
                        <div className="effective-donations-content">
                            {effectiveDonationsLoading ? (
                                <div className="effective-donations-loading">
                                    <i className="fas fa-spinner fa-spin"></i> Carregant donacions...
                                </div>
                            ) : effectiveDonationsError ? (
                                <div className="effective-donations-error">
                                    <i className="fas fa-exclamation-triangle"></i> {effectiveDonationsError}
                                    <button
                                        onClick={fetchEffectiveDonations}
                                        className="effective-donations-retry-button"
                                    >
                                        Tornar a provar
                                    </button>
                                </div>
                            ) : effectiveDonations.length === 0 ? (
                                <div className="effective-donations-empty">
                                    <i className="fas fa-info-circle"></i>
                                    <p>No hi ha donacions efectuades de subscripcions.</p>
                                </div>
                            ) : (
                                <TreasuryTable label="Donatius" className="effective-donations-table-container">
                                    <table className="subscribers-table effective-donations-table">
                                        <thead>
                                            <tr>
                                                <th>Casteller</th>
                                                <th>Quota</th>
                                                <th>Variant</th>
                                                <th>Donació</th>
                                                <th>Pagament total</th>
                                                <th>Data pagament</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {effectiveDonations.map((donation) => {
                                                const castellerName = getCastellerNameWithMote(donation, `Usuari ${donation.casteller_id || ''}`);
                                                const donationAmount = parseFloat(donation.donation_amount || 0);
                                                const paymentAmount = parseFloat(donation.amount || 0);

                                                return (
                                                    <tr key={donation.id}>
                                                        <td className="subscriber-name">{castellerName.trim()}</td>
                                                        <td className="effective-donations-quota-cell">{donation.quota_titol || '-'}</td>
                                                        <td className="effective-donations-variant-cell">{donation.variant_nom || '-'}</td>
                                                        <td>
                                                            <span className="effective-donations-amount-badge">+{formatMoney(donationAmount)}€</span>
                                                        </td>
                                                        <td className="effective-donations-total-cell">
                                                            {formatMoney(paymentAmount)}€
                                                        </td>
                                                        <td className="effective-donations-date-cell">{formatDateTime(donation.paid_at)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </TreasuryTable>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* History Section */}
            {can(userInfo, 'MANAGE_QUOTES') ? (
                <div className="subscriptions-section sub-history-section">
                    <div className="sub-history-card">
                        <div 
                            className="sub-history-header"
                            onClick={() => setShowHistory(!showHistory)}
                        >
                            <h3>
                                <i className="fas fa-history"></i> Historial de canvis
                            </h3>
                            <i className={`fas fa-chevron-${showHistory ? 'up' : 'down'} expand-icon`}></i>
                        </div>
                        
                        {showHistory ? (
                            <div className="sub-history-content">
                                {historyLoading ? (
                                    <div className="sub-history-loading">
                                        <i className="fas fa-spinner fa-spin"></i> Carregant historial...
                                    </div>
                                ) : historyEvents.length === 0 ? (
                                    <div className="sub-history-empty">
                                        <i className="fas fa-info-circle"></i>
                                        <p>No hi ha registres d'historial encara.</p>
                                    </div>
                                ) : (
                                    <>
                                        <TreasuryTable label="Historial de subscripcions" className="sub-history-table-container">
                                            <table className="subscribers-table sub-history-table">
                                                <thead>
                                                    <tr>
                                                        <th>Casteller</th>
                                                        <th>Acció</th>
                                                        <th>Quota</th>
                                                        <th>Data</th>
                                                        <th>Realitzat per</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyEvents.map((event) => {
                                                        const eventInfo = getEventInfo(event.event_type);
                                                        const castellerName = getCastellerNameWithMote(event, `Usuari ${event.casteller_id || ''}`);
                                                        
                                                        const performedByName = event.performed_by_nom 
                                                            ? getCastellerFullName({ nom: event.performed_by_nom, cognom: event.performed_by_cognom }, '-')
                                                            : '-';

                                                        return (
                                                            <tr key={event.id}>
                                                                <td className="subscriber-name">{castellerName}</td>
                                                                <td>{eventInfo.badge}</td>
                                                                <td className="sub-history-quota-cell">
                                                                    <i className="fas fa-file-invoice" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                                                    {event.quota_titol}
                                                                </td>
                                                                <td className="sub-history-date-cell">
                                                                    {formatDateTime(event.created_at)}
                                                                </td>
                                                                <td className="sub-history-user-cell">
                                                                    {event.performed_by_nom ? (
                                                                        <>
                                                                            <i className="fas fa-user-shield" style={{ marginRight: '6px', color: '#94a3b8' }}></i>
                                                                            {performedByName}
                                                                        </>
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </TreasuryTable>
                                        
                                        {/* Pagination */}
                                        {historyTotal > HISTORY_PAGE_SIZE ? (
                                            <div className="sub-history-pagination">
                                                <button
                                                    className="sub-pagination-btn"
                                                    onClick={() => fetchHistory(historyPage - 1)}
                                                    disabled={historyPage === 0 || historyLoading}
                                                >
                                                    <i className="fas fa-chevron-left"></i> Anterior
                                                </button>
                                                <span className="sub-pagination-info">
                                                    {historyPage * HISTORY_PAGE_SIZE + 1} - {Math.min((historyPage + 1) * HISTORY_PAGE_SIZE, historyTotal)} de {historyTotal}
                                                </span>
                                                <button
                                                    className="sub-pagination-btn"
                                                    onClick={() => fetchHistory(historyPage + 1)}
                                                    disabled={(historyPage + 1) * HISTORY_PAGE_SIZE >= historyTotal || historyLoading}
                                                >
                                                    Següent <i className="fas fa-chevron-right"></i>
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default SubscriptionsTab;
